'use client';

/**
 * Games section of the partner catalogue — matches the multicade prototype
 * (docs/cowork-screenshots/partner_landing_page_prototype.png): a "Games" header
 * with category filter pills, a 2-column grid of large cards (game art on top,
 * title + subtitle below), and a "+ N more games" expander.
 *
 * Client component (the parent catalogue stays a server component) because the
 * pills, the collapsed/expanded state, and the expander are all interactive.
 * Categories are derived from each game's `section` (falling back to the first
 * segment of its subtitle, e.g. "Cards · Tournaments" → "Cards"); pills only
 * appear when there is more than one.
 */

import {useEffect, useMemo, useState} from 'react';
import Image from 'next/image';
import type {CSSProperties} from 'react';
import type {CatalogueGame} from './PartnerCatalogue';

// Show enough before "show more" that the featured/non-featured checkerboard is visible
// unexpanded — 8 = 2 full rows on the 4-col desktop / 4 rows on the 2-col phone, and
// (with featured usually positioned first in the catalog) enough to include non-featured
// tiles so the waterfall reads.
const INITIAL_COUNT = 8;

/**
 * Staggered cell entrance for the grid — a category swap and "show more" both cascade
 * in instead of hard-swapping. Pure CSS (the `cellIn` keyframe/animation in
 * tailwind.config.js): each cell gets the `animate-cellIn` class plus an inline
 * animation-delay, so only opacity + transform animate (stays on the compositor).
 * Was framer-motion; dropping it removes framer from the partner route bundle (its
 * only other user, games/[gid]/_src/SkillzSDK/AnimatedStack.tsx, is a different route
 * chunk). The framer version also cascaded cells OUT on a swap (AnimatePresence
 * `mode='wait'`); a same-key swap can't orchestrate an exit in CSS, so the outgoing
 * grid is replaced at once and only the incoming one cascades — the enter feel is
 * unchanged, the (subtle) exit is not replicated.
 */
/** 35ms between neighbours… */
const CELL_STAGGER_S = 0.035;
/** …capped at 6 steps (210ms), so a 20-game grid cascades instead of crawling. */
const CELL_STAGGER_CAP = 6;

const staggerDelay = (steps: number) =>
  Math.min(Math.max(steps, 0), CELL_STAGGER_CAP) * CELL_STAGGER_S;

function gameHref(gameId: number, slug: string, rootMode?: boolean) {
  // Root ("/") surface is vanilla Skillz — no partner attribution param.
  return rootMode ? `/games/${gameId}/` : `/games/${gameId}/?partner=${encodeURIComponent(slug)}`;
}

/**
 * Staggered waterfall: distribute games into `cols` vertical columns in a CHECKERBOARD
 * — cell (row, col) is a FEATURED (big) tile when `(row + col)` is even, else a
 * non-featured (app-icon) tile. So the top-left is featured, top-right isn't, the next
 * row flips, etc. Because featured tiles are tall (9:16) and non-featured are short
 * (square), adjacent columns fall out of phase and the whole grid flows like a real
 * masonry. Featured/non-featured are pulled in their existing order; when one runs out
 * the other fills the remaining cells (so it degrades gracefully to a plain stack).
 */
function buildStaggeredColumns(games: CatalogueGame[], cols: number): CatalogueGame[][] {
  const featured = games.filter((g) => g.featured);
  const rest = games.filter((g) => !g.featured);
  const columns: CatalogueGame[][] = Array.from({length: cols}, () => []);
  let fi = 0;
  let ri = 0;
  let placed = 0;
  const total = games.length;
  for (let row = 0; placed < total; row++) {
    for (let c = 0; c < cols && placed < total; c++) {
      const wantFeatured = (row + c) % 2 === 0;
      // Take the wanted kind; fall back to the other when its queue is empty.
      const pick =
        wantFeatured
          ? fi < featured.length
            ? featured[fi++]
            : rest[ri++]
          : ri < rest.length
            ? rest[ri++]
            : featured[fi++];
      columns[c].push(pick);
      placed++;
    }
  }
  return columns;
}

/** Column count: 2 on phone, 4 on the desktop arcade home (4 cols keeps the cells compact
 *  — ~25% narrower than 3). Client-measured (this is a client component) so the staggered
 *  distribution matches the rendered column count; SSR/first paint use 2, then desktop
 *  reflows to 4 on mount. */
function useColumnCount(): number {
  const [cols, setCols] = useState(2);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setCols(mq.matches ? 4 : 2);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return cols;
}

function categoryOf(game: CatalogueGame): string | null {
  if (game.section) return game.section;
  const first = game.subtitle?.split('·')[0]?.trim();
  return first || null;
}

/**
 * Chip label. `section` comes straight off the published catalog and is not
 * guaranteed to be cased for display — Lycos publishes a `top` section, which
 * rendered as a lowercase chip next to Title Case ones. Filtering still matches
 * on the raw value; only the label is normalized.
 */
function categoryLabel(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export default function CatalogueGames({
  games,
  slug,
  rootMode,
}: {
  games: CatalogueGame[];
  slug: string;
  rootMode?: boolean;
}) {
  const [active, setActive] = useState('All');
  const [expanded, setExpanded] = useState(false);
  // Index the current transition cascades FROM: 0 for a category swap (every cell is
  // new, so the whole grid cascades), the previously-visible count for "show more"
  // (only the newly revealed cells fly in). A cell animates only when its grid index
  // is >= this, so the already-visible cells stay put on "show more".
  const [animateFrom, setAnimateFrom] = useState(0);
  // Only animate AFTER a user transition (category swap / "show more"). The initial
  // load must not animate: the catalogue is page content, not an entrance, and animating
  // from opacity:0 on hydrate would flash the server-painted tiles. Gating on interaction
  // (not "mounted") is deliberate — the desktop column reflow (useColumnCount 2→4) is a
  // post-mount re-render that a mount flag would wrongly treat as an entrance.
  const [hasInteracted, setHasInteracted] = useState(false);

  const categories = useMemo(() => {
    const seen = new Set<string>();
    games.forEach((g) => {
      const c = categoryOf(g);
      // Skip a section literally named "all" — it would render a second "All" chip
      // that collides with the built-in default below.
      if (c && c.toLowerCase() !== 'all') seen.add(c);
    });
    return Array.from(seen);
  }, [games]);

  const filtered = useMemo(
    () => (active === 'All' ? games : games.filter((g) => categoryOf(g) === active)),
    [games, active]
  );

  const visible = expanded ? filtered : filtered.slice(0, INITIAL_COUNT);
  const remaining = filtered.length - visible.length;

  // Staggered checkerboard columns (2 phone / 3 desktop). `orderIndex` keeps the
  // entrance cascade in reading order even though cells are split across columns.
  const cols = useColumnCount();
  const columns = useMemo(() => buildStaggeredColumns(visible, cols), [visible, cols]);
  const orderIndex = useMemo(() => {
    const m = new Map<number, number>();
    visible.forEach((g, i) => m.set(g.gameId, i));
    return m;
  }, [visible]);

  return (
    <section aria-labelledby='partner-games'>
      <div className='mb-4 flex items-center justify-between gap-3'>
        <h2
          id='partner-games'
          className='flex items-center gap-2 text-[18px] font-extrabold sm:text-[22px]'
          style={{fontFamily: 'var(--partner-heading-font, inherit)'}}
        >
          {/* Inline SVG (no external asset to 404) — a gamepad, matching the cup icon on
              the Live Events heading (same 22px stroke style). */}
          <svg
            aria-hidden
            viewBox='0 0 24 24'
            width={22}
            height={22}
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='6' y1='12' x2='10' y2='12' />
            <line x1='8' y1='10' x2='8' y2='14' />
            <line x1='15' y1='13' x2='15.01' y2='13' />
            <line x1='18' y1='11' x2='18.01' y2='11' />
            <rect x='2' y='6' width='20' height='12' rx='3' />
          </svg>
          Games
        </h2>
        {categories.length > 1 ? (
          // `no-scrollbar` is the project's existing cross-browser utility
          // (src/styles/globals.css @layer utilities: `scrollbar-width: none` +
          // `-ms-overflow-style: none` + `::-webkit-scrollbar {display: none}`).
          // The row stays `overflow-x-auto`, so touch / trackpad / shift-wheel
          // scrolling still works — only the visible bar goes away.
          <div className='no-scrollbar flex items-center gap-2 overflow-x-auto'>
            {['All', ...categories].map((cat) => {
              const isActive = cat === active;
              return (
                <button
                  key={cat}
                  type='button'
                  onClick={() => {
                    if (cat === active) return;
                    // A swap replaces every cell, so the cascade starts at index 0.
                    setHasInteracted(true);
                    setAnimateFrom(0);
                    setActive(cat);
                    setExpanded(false);
                  }}
                  className='shrink-0 rounded-full px-3 py-[5px] text-[10.5px] transition-colors sm:px-4 sm:py-[7px] sm:text-[12px]'
                  style={
                    isActive
                      ? {
                          backgroundColor: 'var(--partner-accent)',
                          color: 'var(--partner-accent-text)',
                          fontWeight: 800,
                        }
                      : {
                          backgroundColor: 'var(--partner-chip-bg)',
                          color: 'var(--partner-chip-text)',
                          fontWeight: 600,
                        }
                  }
                >
                  {categoryLabel(cat)}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Staggered waterfall: explicit flex COLUMNS (not CSS multi-column) so tiles fill
          left-to-right in reading order and we control the featured/non-featured
          checkerboard. Tall featured (9:16) tiles beside short non-featured (square)
          tiles push the columns out of phase into a flowing masonry.
          `key={active}` remounts the whole grid on a category swap, so every cell is a
          fresh mount and the `animate-cellIn` class cascades the entire grid in. "Show
          more" keeps the key, so only the newly added cells (index >= animateFrom) mount
          and animate; the ones already on screen stay put. */}
      <div key={active} className='flex gap-3 lg:gap-[18px]'>
        {columns.map((col, ci) => (
          <div key={ci} className='flex flex-1 flex-col gap-3 lg:gap-[18px]'>
            {col.map((game) => {
              const gi = orderIndex.get(game.gameId) ?? 0;
              // Animate a cell only after a user transition (never on load) AND only if
              // it's at/after the cascade origin (so "show more" doesn't re-animate the
              // cells already visible). motion-reduce disables it in CSS.
              const willAnimate = hasInteracted && gi >= animateFrom;
              return (
                <div
                  key={game.gameId}
                  className={willAnimate ? 'animate-cellIn motion-reduce:animate-none' : undefined}
                  style={willAnimate ? {animationDelay: `${staggerDelay(gi - animateFrom)}s`} : undefined}
                >
                  <GameCard game={game} slug={slug} priority={gi < 4} rootMode={rootMode} />
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {remaining > 0 ? (
        <button
          type='button'
          onClick={() => {
            // Only the cells past the ones already on screen should fly in.
            setHasInteracted(true);
            setAnimateFrom(visible.length);
            setExpanded(true);
          }}
          className='mt-3 flex w-full items-center justify-center gap-2 rounded-[13px] border border-dashed py-3.5 text-[13px] font-extrabold transition-colors hover:bg-black/[0.03]'
          style={{borderColor: 'var(--partner-card-border)', color: 'var(--partner-text-muted)'}}
        >
          <span aria-hidden className='text-lg leading-none'>
            +
          </span>
          {remaining} more game{remaining === 1 ? '' : 's'}
        </button>
      ) : null}
    </section>
  );
}

/** "3.2K playing" — thousands compacted (860 → "860", 3200 → "3.2K"). */
function playersLabel(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(n);
}

// "never theme" green for money/live (design §9); on white surfaces the overlay
// scrim keeps it dark enough for the on-dark value to hold, so one value is fine.
const OVERLAY_GREEN = '#3ddc97';

function GameCard({
  game,
  slug,
  priority,
  rootMode,
}: {
  game: CatalogueGame;
  slug: string;
  priority: boolean;
  rootMode?: boolean;
}) {
  const hasVideo = game.featured && !!game.featuredVideo;
  // Featured tiles are BIG (tall): 9:16 trailer, else a 3:4 key-art card. Non-featured
  // tiles are the APP ICON at a compact square — the size contrast is what staggers the
  // checkerboard columns into a waterfall.
  const boxAspect = hasVideo ? 'aspect-[9/16]' : game.featured ? 'aspect-[3/4]' : 'aspect-square';
  const imgSrc = game.featured ? game.featuredImage || game.iconThumbnail : game.iconThumbnail;

  return (
    <a
      href={gameHref(game.gameId, slug, rootMode)}
      aria-label={game.title}
      // Overlay treatment (design v10 §3.5): text sits ON the creative behind a bottom
      // scrim — one visual language with the Live Events cards. The resting border rides
      // on a custom property, not an inline style, so the `hover:` accent rule can win.
      className='group relative block w-full overflow-hidden rounded-[13px] border border-[color:var(--card-border)] outline-none transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-[3px] hover:border-[color:var(--partner-accent)] hover:shadow-md focus-visible:ring-2'
      style={{'--card-border': 'var(--partner-card-border)', backgroundColor: 'var(--partner-card)'} as CSSProperties}
    >
      <div className={`relative w-full overflow-hidden ${boxAspect}`}>
        {hasVideo ? (
          // poster is MANDATORY (§3.5) so the tile never renders empty while loading.
          <video
            src={game.featuredVideo}
            poster={game.featuredImage || game.iconThumbnail}
            autoPlay
            muted
            loop
            playsInline
            preload='auto'
            className='absolute inset-0 h-full w-full object-cover'
          />
        ) : (
          <Image
            src={imgSrc}
            alt={game.title}
            fill
            sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px'
            priority={priority}
            className='object-cover'
          />
        )}

        {/* Live-players pill, top-left (§3.5). Rendered only with a real count. */}
        {typeof game.livePlayers === 'number' ? (
          <span className='absolute left-2 top-2 flex items-center gap-1 rounded-full bg-[rgba(6,6,12,0.68)] px-2 py-[3px] text-[8.5px] font-extrabold text-white lg:text-[9.5px]'>
            <span className='h-[5px] w-[5px] rounded-full' style={{backgroundColor: OVERLAY_GREEN}} />
            {playersLabel(game.livePlayers)} playing
          </span>
        ) : null}

        {/* Muted-audio glyph, top-right, on video tiles — the affordance that audio exists. */}
        {hasVideo ? (
          <span
            aria-hidden
            className='absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(6,6,12,0.62)]'
          >
            <svg viewBox='0 0 24 24' width={13} height={13} fill='none' stroke='#fff' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
              <path d='M11 5 6 9H2v6h4l5 4z' />
              <path d='m23 9-6 6M17 9l6 6' />
            </svg>
          </span>
        ) : null}

        {/* Bottom scrim + overlaid title/prize. Category is dropped in overlay mode (§3.5):
            the scrim must work on the shortest tile, so title + prize is the irreducible pair. */}
        <div
          className='absolute inset-x-0 bottom-0 flex flex-col gap-0.5 px-2.5 pb-2 pt-6 lg:px-3 lg:pb-2.5'
          style={{
            background:
              'linear-gradient(180deg, rgba(8,8,14,0) 0%, rgba(8,8,14,0.88) 55%, rgba(8,8,14,0.96) 100%)',
          }}
        >
          <span className='line-clamp-1 text-[12.5px] font-extrabold leading-tight text-white lg:text-[14.5px]'>
            {game.title}
          </span>
          {game.prizeCeiling ? (
            <span
              className='text-[9.5px] font-extrabold leading-tight lg:text-[11.5px]'
              style={{color: OVERLAY_GREEN}}
            >
              Up to {game.prizeCeiling}
            </span>
          ) : null}
        </div>
      </div>
    </a>
  );
}
